// Constants
import { PlanType } from "@ms/uno-constants/lib/local/AppConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { LeftNavStaticOptions, NavView } from "@ms/uno-constants/lib/local/LeftNavConstants";
import { SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { ElementIds } from "@ms/uno-constants/lib/local/UIConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { useLoggers } from "@ms/planner-presentational-controls/lib/local/utilities/hooks/useLoggers";
import { LeftNav } from "../../presentational/leftNav/LeftNav";
import { LeftNavNewPlanSplitMenuOptions } from "../../presentational/leftNav/LeftNavNewPlanSection";
// Hooks
import { useActionCreators } from "@ms/planner-container-controls/lib/local/hooks/useActionCreators";
import { useConfigProvider } from "@ms/planner-container-controls/lib/local/hooks/useConfigProvider";
import { useRenderTracker } from "@ms/planner-container-controls/lib/local/hooks/useRenderTracker";
import { useStores } from "@ms/planner-container-controls/lib/local/hooks/useStores";
import { MruApp } from "@ms/planner-models/lib/local/service/mru/MruApp";
// Styles
import { MyDayFilled, MyDayRegular, MyPlansFilled, MyPlansRegular, MyTasksFilled, MyTasksRegular, PortfolioFolder, PortfolioFolderFilled } from "@ms/planner-generic-controls/lib/local/icons/index";
// Utilities
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
import { isMruItemAPlan } from "@ms/planner-models/lib/local/utilities/MruAppUtilities";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { isStateFailure } from "@ms/planner-stores/lib/local/utilities/StatusUtilities";
import { ModalType } from "@ms/uno-constants/lib/local/ModalType";
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { extractEntityRoutingIdFromSubRoute } from "@ms/uno-routing";
import { safelyOpenExternalUrl } from "@ms/uno-utilities/lib/local/BrowserUtilities";
import React, { useEffect } from "react";
import { Strings, loadTranslations } from "../../../utilities/CultureUtilities";
import { checkForMyPortfolioOptionVisibility, comparePinnedItem, getBreadcrumbItems, getContainerPhoto } from "../../../utilities/LeftNavUtilities";
import { calculateLeftNavRenderState } from "./LeftNavContainer.perf";
/**
 * Left Nav container top level smart component
 */ export const LeftNavContainer = ()=>{
    const configProvider = useConfigProvider();
    const { engagementLogger, traceLogger } = useLoggers();
    // State for portfolio callout visibility
    const [isNewPortfolioCalloutVisible, setIsNewPortfolioCalloutVisible] = React.useState(false);
    const [currentView, setCurrentView] = React.useState(NavView.TasksView);
    const [appContextStore, modernGroupsStore, mruStore, orgStore, routeStore, teamsStore, teamsChannelStore, tenantStore, viewStore, premiumPlanPermissionsStore, portfolioStore, todoStore, rosterStore, userStore, planStore, driveItemStore] = useStores([
        Store.AppContext,
        Store.ModernGroups,
        Store.Mru,
        Store.Org,
        Store.Route,
        Store.Teams,
        Store.TeamsChannel,
        Store.Tenant,
        Store.View,
        Store.PremiumPlanPermissions,
        Store.Portfolio,
        Store.Todo,
        Store.Roster,
        Store.User,
        Store.Plan,
        Store.DriveItem
    ]);
    const [modalActionCreator, mruActionCreator, routeActionCreator, viewTransformsActionCreator] = useActionCreators([
        ActionCreator.Modal,
        ActionCreator.Mru,
        ActionCreator.Routing,
        ActionCreator.ViewTransforms
    ]);
    // Hook to track the view render state of the left nav
    useRenderTracker({
        name: ViewName.LeftNavContainer,
        partitionKey: appContextStore.getCurrentUserIdentitySet().user.id
    }, calculateLeftNavRenderState);
    // Fetch critical data and load strings on mount
    useEffect(()=>{
        // Load translations based on current locale at mount time
        loadTranslations(appContextStore.getAppContext().locale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /**
     * Callback when a leftNav pinned collection is selected
     * @param mruItem Mru item that was selected
     */ const onSelectCollection = (mruItem)=>{
        traceLogger.logTrace(0x1e15605c /* tag_4fwb2 */ , TraceLevel.Info, `LeftNav pinned collection selected [CollectionId=${mruItem.serverDocId}][MruId=${mruItem.mruId}][Type=${mruItem.app}]`);
        engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.OptionClicked, {
            option: `${mruItem.app}`
        });
        // TODO: we need to revisit the following logic if there's any changes on mru item type
        switch(mruItem.app){
            case MruApp.Planner:
                routeActionCreator.navigateToPlan({
                    planId: mruItem.serverDocId,
                    planType: PlanType.Basic,
                    planName: mruItem.title
                });
                break;
            case MruApp.Project:
                {
                    const typedProjectMetadata = mruItem.metadata;
                    if (mruItem.isProjectOnlinePlan() || mruItem.isProjectOperationsPlan()) {
                        engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.PlanClicked, {
                            metadataType: `${typedProjectMetadata.metadataType}`
                        });
                        safelyOpenExternalUrl(mruItem.url, traceLogger);
                    } else if (mruItem.isNamedOrgPlan()) {
                        const orgId = typedProjectMetadata.orgId;
                        routeActionCreator.navigateToPlan({
                            orgId: orgId ?? "",
                            planId: mruItem.serverDocId,
                            planType: PlanType.Premium,
                            planName: mruItem.title
                        });
                        engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.PlanClicked, {
                            metadataType: `${typedProjectMetadata.metadataType}`,
                            orgType: `${typedProjectMetadata.orgType}`
                        });
                    } else {
                        let orgId = typedProjectMetadata.orgId ?? orgStore?.getDefaultCdsInstance().data?.orgId;
                        if (orgId == null) {
                            traceLogger.logTrace(0x1e15605b /* tag_4fwb1 */ , TraceLevel.Warning, `LeftNav project selected but orgId was not found [ProjectId=${mruItem.serverDocId}]`);
                            // Fallback for now to current behavior
                            orgId = typedProjectMetadata.orgType;
                        }
                        routeActionCreator.navigateToPlan({
                            orgId: orgId,
                            planId: mruItem.serverDocId,
                            planType: PlanType.Premium,
                            planName: mruItem.title
                        });
                    }
                    break;
                }
            case MruApp.ToDo:
                routeActionCreator.navigateToPlan({
                    planId: mruItem.serverDocId,
                    planType: PlanType.ToDo,
                    planName: mruItem.title
                });
                break;
            default:
                traceLogger.logTrace(0x1e15605a /* tag_4fwb0 */ , TraceLevel.Warning, `LeftNav unsupported collection type selected [CollectionId=${mruItem.serverDocId}][MruId=${mruItem.mruId}][Type=${mruItem.app}]`);
                return;
        }
    };
    /**
     * Callback when the unpin button is clicked
     * @param serverDocId ServerDocId of the mru item
     */ const onUnpinClick = (serverDocId)=>{
        engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.PlanPinned, {
            isPinned: "false"
        });
        const mruItem = mruStore?.getMruItemByServerDocId(serverDocId);
        if (mruItem == null) {
            traceLogger.logTrace(0x1e156059 /* tag_4fwbz */ , TraceLevel.Warning, `LeftNav unpin clicked but mru item was not present in the store [ServerDocId=${serverDocId}]`);
            return;
        }
        const updatedMruItem = mruItem.setProperty("isPinned", false);
        mruActionCreator.upsertMruItem(updatedMruItem);
    };
    /**
     * Callback when a create new option is selected
     * @param optionType Type of option to create - plan or portfolio
     * @param target The target element on which the callout is shown
     */ const onSelectNewOption = (optionType)=>{
        if (optionType === LeftNavNewPlanSplitMenuOptions.Plan) {
            engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.NewPlanButtonClicked);
            modalActionCreator.openModal({
                modalType: ModalType.CreatePlan,
                showCreateNewPremium: false,
                selectedTemplateType: undefined,
                onDismiss: undefined
            });
        } else if (optionType === LeftNavNewPlanSplitMenuOptions.Portfolio) {
            engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.NewPortfolioButtonClicked);
            if (premiumPlanPermissionsStore.userHasP3PlusLicense() && hasTenantEnabledPortfolios()) {
                modalActionCreator.openModal({
                    modalType: ModalType.CreatePortfolio
                });
            } else {
                setIsNewPortfolioCalloutVisible(true);
            }
        }
    };
    /**
     * Callback when the switch view dropdown is clicked
     */ const onSwitchView = (option)=>{
        setCurrentView(option);
        const targetSubRouteType = option === NavView.PublisherView ? SubRouteType.Publishing : SubRouteType.MyTasks;
        traceLogger.logTrace(0x1e156058 /* tag_4fwby */ , TraceLevel.Info, `LeftNav view switched [View=${targetSubRouteType}]`);
        engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.SwitchView, {
            view: `${targetSubRouteType}`
        });
        routeActionCreator.navigate({
            subRouteType: targetSubRouteType,
            subRoute: []
        });
    };
    /**
     * Callback when a leftNav static option is selected
     * @param option Id of the static option
     */ const onSelectStaticOption = (option)=>{
        traceLogger.logTrace(0x1e156057 /* tag_4fwbx */ , TraceLevel.Info, `LeftNav static option selected [Option=${option}]`);
        switch(option){
            case LeftNavStaticOptions.MyDay:
                engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.OptionClicked, {
                    option: `${LeftNavStaticOptions.MyDay}`
                });
                routeActionCreator.navigate({
                    subRouteType: SubRouteType.MyDay,
                    subRoute: []
                });
                break;
            case LeftNavStaticOptions.MyPlans:
                engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.OptionClicked, {
                    option: `${LeftNavStaticOptions.MyPlans}`
                });
                routeActionCreator.navigate({
                    subRouteType: SubRouteType.MyPlans,
                    subRoute: []
                });
                break;
            case LeftNavStaticOptions.MyPortfolios:
                engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.OptionClicked, {
                    option: `${LeftNavStaticOptions.MyPortfolios}`
                });
                routeActionCreator.navigate({
                    subRouteType: SubRouteType.MyPortfolios,
                    subRoute: []
                });
                break;
            case LeftNavStaticOptions.MyTasks:
                engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.OptionClicked, {
                    option: `${LeftNavStaticOptions.MyTasks}`
                });
                routeActionCreator.navigate({
                    subRouteType: SubRouteType.MyTasks,
                    subRoute: []
                });
                break;
            default:
                /* istanbul ignore next */ ErrorUtilities.unreachableCase(option, (log)=>{
                    traceLogger.logTrace(0x1e156056 /* tag_4fwbw */ , TraceLevel.Error, log);
                });
        }
    };
    /**
     * Generate the static options for Planner LeftNav
     */ const getLeftNavStaticOptions = (currentRouteType, isMyPortfoliosOptionVisible)=>{
        const myDayIcons = {
            normalIcon: /*#__PURE__*/ React.createElement(MyDayRegular, null),
            focusedIcon: /*#__PURE__*/ React.createElement(MyDayFilled, null)
        };
        const myDayStaticOption = {
            id: ElementIds.MyDayNavButton,
            name: Strings.LeftNav_MyDayTitle,
            selected: currentRouteType === SubRouteType.MyDay,
            icon: myDayIcons,
            onSelect: ()=>onSelectStaticOption(LeftNavStaticOptions.MyDay)
        };
        const myTasksIcons = {
            normalIcon: /*#__PURE__*/ React.createElement(MyTasksRegular, null),
            focusedIcon: /*#__PURE__*/ React.createElement(MyTasksFilled, null)
        };
        const myTasksStaticOption = {
            id: ElementIds.MyTasksNavButton,
            name: Strings.LeftNav_MyTasksTitle,
            selected: currentRouteType === SubRouteType.MyTasks,
            icon: myTasksIcons,
            onSelect: ()=>onSelectStaticOption(LeftNavStaticOptions.MyTasks)
        };
        const myPlanIcons = {
            normalIcon: /*#__PURE__*/ React.createElement(MyPlansRegular, null),
            focusedIcon: /*#__PURE__*/ React.createElement(MyPlansFilled, null)
        };
        const myPlanStaticOption = {
            id: ElementIds.MyPlansNavButton,
            name: Strings.LeftNav_MyPlansTitle,
            selected: currentRouteType === SubRouteType.MyPlans,
            icon: myPlanIcons,
            onSelect: ()=>onSelectStaticOption(LeftNavStaticOptions.MyPlans)
        };
        const myPortfolioIcons = {
            normalIcon: /*#__PURE__*/ React.createElement(PortfolioFolder, null),
            focusedIcon: /*#__PURE__*/ React.createElement(PortfolioFolderFilled, null)
        };
        const myPortfolioStaticOption = {
            id: ElementIds.MyPortfoliosNavButton,
            name: Strings.LeftNav_MyPortfoliosTitle,
            selected: currentRouteType === SubRouteType.MyPortfolios,
            icon: myPortfolioIcons,
            onSelect: ()=>onSelectStaticOption(LeftNavStaticOptions.MyPortfolios)
        };
        const staticOptionsList = [
            myDayStaticOption,
            myTasksStaticOption,
            myPlanStaticOption
        ];
        if (isMyPortfoliosOptionVisible) {
            staticOptionsList.push(myPortfolioStaticOption);
        }
        const leftNavStaticOptions = {
            staticOptions: staticOptionsList
        };
        return leftNavStaticOptions;
    };
    /**
     * Get the pinned options for Planner LeftNav
     */ const getLeftNavPinnedOptions = ()=>{
        const currentRouteType = routeStore.getCurrentSubRouteType();
        const currentSubRoute = routeStore.getCurrentSubRoute();
        const pinnedItems = mruStore.getPinnedItems().data;
        const currentSelectedPlanId = extractEntityRoutingIdFromSubRoute(currentSubRoute, currentRouteType);
        // Filter out pinned items that are not plans. Sort the pinned items in descending order of pinned time stamp
        const filteredAndSortedPinnedItems = pinnedItems.filter((pinnedItem)=>isMruItemAPlan(pinnedItem)).sort(comparePinnedItem);
        return filteredAndSortedPinnedItems.map((mruItem)=>{
            // Find the associated item in the retail services tabs, if any
            const tab = teamsStore?.getTeamsTasksTabsForPlan(mruItem.serverDocId)?.[0] ?? null;
            const tabChannelId = tab?.channelId ?? null;
            const containerType = mruItem.getContainerType();
            const containerStores = {
                teamsChannelStore,
                modernGroupsStore,
                userStore,
                rosterStore,
                todoStore,
                appContextStore,
                planStore,
                driveItemStore
            };
            return {
                id: mruItem.serverDocId,
                name: mruItem.title,
                selected: currentSelectedPlanId === mruItem.serverDocId,
                breadcrumbItems: getBreadcrumbItems(mruItem, tabChannelId, containerStores),
                onTogglePin: ()=>onUnpinClick(mruItem.serverDocId),
                containerType: containerType ?? undefined,
                containerPhoto: getContainerPhoto(mruItem.getContainerId() ?? "", containerType, modernGroupsStore) ?? undefined,
                onSelect: ()=>onSelectCollection(mruItem)
            };
        });
    };
    /**
     * Fetches the pinned and recent mru items
     */ const fetchMruItems = ()=>{
        mruActionCreator.fetchMruItems();
    };
    /**
     * Method to check if portfolios are enabled
     */ const hasTenantEnabledPortfolios = ()=>!!tenantStore.getCurrentTenantPortfoliosEnabledSetting().data;
    /**
     * Get the callout details for the portfolio option
     */ const getCalloutDetails = ()=>{
        const isPortfoliosEnabled = hasTenantEnabledPortfolios();
        if (!isPortfoliosEnabled) {
            return {
                calloutTitle: Strings.LeftNav_NewPlanPortfolioNotEnabledCalloutTitle,
                calloutDescription: Strings.LeftNav_NewPlanPortfolioNotEnabledCalloutDescription,
                calloutLinkText: ""
            };
        }
        return {
            calloutTitle: Strings.LeftNav_NewPlanCalloutTitle,
            calloutDescription: Strings.LeftNav_NewPlanCalloutDescription,
            calloutLinkText: Strings.LeftNav_NewPlanCalloutLinkText
        };
    };
    /**
     * Method to check if the error message should be rendered for the pinned section
     */ const getShouldRenderPinnedError = ()=>{
        const pinnedItems = mruStore.getPinnedItems();
        return pinnedItems.data.length === 0 && isStateFailure(pinnedItems.status.state);
    };
    /**
     * Method to set the left nav collapsed state
     * @param isCollapsed The collapsed state to set
     */ const setIsLeftNavCollapsed = (isCollapsed)=>{
        viewTransformsActionCreator.setLeftNavExpanded(!isCollapsed);
    };
    // Generate new plan option for Left Nav
    const getLeftNavNewPlanInfo = ()=>{
        return {
            onSelect: (optionType)=>onSelectNewOption(optionType),
            // disable portfolio experience for FR/DE environment
            isSplitButtonEnabled: configProvider().flights.EnablePortfolioExperience && configProvider().settings.EnvironmentFeatureSettings.EnablePortfolioExperience,
            // Toggling the callout visibility using isNewPortfolioCalloutVisible.
            // Change in state triggers a re-render of component toggling callout visibility.
            onDismissCallout: ()=>{
                setIsNewPortfolioCalloutVisible(false);
            },
            isNewPortfolioCalloutVisible: isNewPortfolioCalloutVisible
        };
    };
    /**
     * Get the options for the switch view dropdown
     */ const switchViewDropdownOptions = {
        onChange: onSwitchView,
        // Since publishing is disabled for Evo, setting it to false
        isPublisherViewVisible: false,
        currentView: currentView
    };
    const shouldRenderPinnedError = getShouldRenderPinnedError();
    const calloutDetails = getCalloutDetails();
    const isFullFrameMode = appContextStore.isFullFrameMode();
    return /*#__PURE__*/ React.createElement(LeftNav, {
        staticOptions: getLeftNavStaticOptions(routeStore.getCurrentSubRouteType(), checkForMyPortfolioOptionVisibility(configProvider, premiumPlanPermissionsStore, portfolioStore)),
        pinnedOptions: getLeftNavPinnedOptions(),
        newPlanInfo: getLeftNavNewPlanInfo(),
        switchViewDropdownOptions: switchViewDropdownOptions,
        shouldRenderPinnedError: shouldRenderPinnedError,
        onRetryButtonClicked: fetchMruItems,
        showAppInfo: !isFullFrameMode,
        calloutDetails: calloutDetails,
        isLeftNavCollapsed: !viewStore.getLeftNavExpanded(),
        setIsLeftNavCollapsed: setIsLeftNavCollapsed,
        isAppInBackground: appContextStore.isAppInBackground()
    });
};
